import React, {useCallback, useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {toast} from 'react-toastify'
import {ToastDefaultConfig} from 'setup/toast/ToastConfig'
import {KTSVG} from 'app/utils/helpers'
import ServiceCategoryModal from '../category-modal/ServiceCategoryModal'
import AddMajorServiceModal from '../service-form/AddMajorServiceModal'
import AddSubServiceModal from '../service-form/AddSubServiceModal'
import AddSubSubServiceModal from '../service-form/AddSubSubServiceModal'
import './style.scss'
import {
  GetSubServiceRequest,
  GetSubSubServiceRequest,
  ServiceTreeNode,
  ServiceLevel,
} from '../../models'
import {CATEGORY_TYPE} from 'app/utils/common/constants/Config'
import {useAppDispatch} from 'setup/redux/Hooks'
import {serviceManagementAction} from '../../redux/ServiceManagementSlice'
import { setError } from 'app/modules/trip/redux/TripSlice'

const ICON_BASE = '/assets/images/icons'

const ICONS = {
  chevronRight: `${ICON_BASE}/chevron-right-line.svg`,
  chevronDown: `${ICON_BASE}/chevron-down-line.svg`,
  pencil: `${ICON_BASE}/pencil-line.svg`,
  plus: `${ICON_BASE}/plus-line.svg`,
  lock: `${ICON_BASE}/lock-line.svg`,
}

type CategoryFilter = 'ALL' | 'B8AK' | 'EXTERNAL'
// Every tree node is explicitly one of these four levels - never inferred
// from nesting/position.
type NodeType = 'category' | 'service' | 'subService' | 'subSubService'
type ApiTreeNode = ServiceTreeNode & {
  treeKey: string
  parentTreeKey?: string
  nodeType: NodeType
  categoryInfoId: string
  serviceId?: number
  subServiceId?: number
  b8ShareCategoryInfoId?: string
  // From the parent list API, so the chevron is known before the children are fetched.
  hasChildren: boolean
  // Unpublished or dropped from the Category scope on B8ak; shown but frozen.
  isRemovedFromB8ak: boolean
  // Only the topmost removed node carries the label; its descendants are just greyed out.
  showRemovedLabel: boolean
}

// CNT-10 - CentroServiceConfig is keyed by (ServiceLevel, ServiceId), so a tree
// node maps to the numeric catalogue id of its own level, not to its row UUID.
const getServiceLevel = (node: ApiTreeNode): ServiceLevel | null => {
  if (node.nodeType === 'service') return 1
  if (node.nodeType === 'subService') return 2
  if (node.nodeType === 'subSubService') return 3
  return null
}

const getServiceNumericId = (node: ApiTreeNode): number | null => {
  const raw =
    node.nodeType === 'service'
      ? node.serviceId
      : node.nodeType === 'subService'
      ? node.subServiceId
      : (node as any).subSubServiceId

  return raw !== undefined && raw !== null && Number.isFinite(Number(raw)) ? Number(raw) : null
}

// A + on a node creates a child one level below it.
const getChildLevel = (node: ApiTreeNode): ServiceLevel | null => {
  if (node.nodeType === 'category') return 1
  if (node.nodeType === 'service') return 2
  if (node.nodeType === 'subService') return 3
  return null
}

const canAddChild = (node: ApiTreeNode): boolean => {
  // A Fixed (B8AK) category still takes Custom Major Services; only B8AK services stay locked.
  if (node.sourceType === CATEGORY_TYPE.B8AK && node.nodeType !== 'category') return false
  switch (getChildLevel(node)) {
    case 1:
      return Boolean(node.categoryInfoId)

    case 2:
      return Number.isFinite(Number(node.serviceId))

    case 3:
      return Number.isFinite(Number(node.serviceId)) && Number.isFinite(Number(node.subServiceId))

    default:
      return false
  }
}

const FILTERS: {value: CategoryFilter; labelKey: string}[] = [
  {value: 'ALL', labelKey: 'SERVICE_MANAGEMENT_PAGE.SERVICE_TREE.FILTER_ALL'},
  {value: 'B8AK', labelKey: 'SERVICE_MANAGEMENT_PAGE.SERVICE_TREE.FILTER_B8AK_ONLY'},
  {
    value: 'EXTERNAL',
    labelKey: 'SERVICE_MANAGEMENT_PAGE.SERVICE_TREE.FILTER_MY_SERVICES_ONLY',
  },
]

interface ServiceSourceFiltersProps {
  value: CategoryFilter
  // US-013 / AC-075 - until at least one service exists, All Services is the
  // fixed selection and the source filters cannot be picked.
  sourceFiltersDisabled?: boolean
  onChange?: (value: CategoryFilter) => void
}

// Filters only narrow the displayed view; they never mutate service data.
export const ServiceSourceFilters: React.FC<ServiceSourceFiltersProps> = ({
  value,
  sourceFiltersDisabled = false,
  onChange,
}) => {
  const {t} = useTranslation()

  return (
    <div
      className='service-tree__filters'
      role='group'
      aria-label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_TREE.FILTER_GROUP')}
    >
      {FILTERS.map(({value: option, labelKey}) => {
        const isDisabled = sourceFiltersDisabled && option !== 'ALL'

        return (
          <button
            key={option}
            type='button'
            className={`service-tree__filter${
              value === option ? ' service-tree__filter--active' : ''
            }`}
            aria-pressed={value === option}
            aria-disabled={isDisabled}
            disabled={isDisabled}
            onClick={() => onChange?.(option)}
          >
            {t(labelKey)}
          </button>
        )
      })}
    </div>
  )
}

const COLUMN_KEYS = [
  'SERVICE_MANAGEMENT_PAGE.SERVICE_TREE.TH_SERVICE',
  'SERVICE_MANAGEMENT_PAGE.SERVICE_TREE.TH_DURATION',
  'SERVICE_MANAGEMENT_PAGE.SERVICE_TREE.TH_PRICE',
  'SERVICE_MANAGEMENT_PAGE.SERVICE_TREE.TH_RECEIVE_B8AK_ORDERS',
  'SERVICE_MANAGEMENT_PAGE.SERVICE_TREE.TH_ACTIVE',
  'SERVICE_MANAGEMENT_PAGE.SERVICE_TREE.TH_BOOKING_PAGE',
  'SERVICE_MANAGEMENT_PAGE.SERVICE_TREE.TH_EDIT_ADD',
]

interface ToggleProps {
  checked: boolean
  label: string
  disabled?: boolean
  readOnly?: boolean
  locked?: boolean
  onChange: () => void
}

const Toggle: React.FC<ToggleProps> = ({checked, label, disabled, readOnly, locked, onChange}) => (
  <button
    type='button'
    role='switch'
    aria-checked={checked}
    aria-label={label}
    aria-busy={disabled}
    aria-disabled={locked}
    disabled={disabled || readOnly}
    className={`service-tree__toggle${checked ? ' service-tree__toggle--on' : ''}${
      disabled ? ' service-tree__toggle--busy' : ''
    }${readOnly ? ' service-tree__toggle--readonly' : ''}${
      locked ? ' service-tree__toggle--locked' : ''
    }`}
    onClick={onChange}
  >
    <span className='service-tree__toggle-knob' />
  </button>
)

const mapApiNode = (
  item: any,
  nodeType: NodeType,
  parent?: ApiTreeNode,
  index = 0
): ApiTreeNode => {
  const categoryInfoId =
    nodeType === 'category'
      ? String(item.categoryInfoId ?? item.id)
      : String(item.categoryInfoId ?? parent?.categoryInfoId ?? '')

  const rawServiceId = item.serviceId ?? parent?.serviceId

  const rawSubServiceId = item.subServiceId ?? parent?.subServiceId

  const serviceId =
    rawServiceId !== undefined && rawServiceId !== null ? Number(rawServiceId) : undefined

  const subServiceId =
    rawSubServiceId !== undefined && rawSubServiceId !== null ? Number(rawSubServiceId) : undefined

  // `id` is the UUID used to identify a row in the UI tree.
  // Do not convert it to number or replace it with serviceId/subServiceId.
  const source: ApiTreeNode['source'] =
    item.sourceType === 'B8AK' || item.source === 'B8AK' || item.source === 'b8ak'
      ? 'b8ak'
      : 'custom'

  const id = String(item.id ?? item.categoryInfoId)
  const hasOwnId = (item.id ?? item.categoryInfoId) != null
  const ownKey = hasOwnId ? `${nodeType}-${id}` : `${nodeType}-${index}`
  const treeKey = parent ? `${parent.treeKey}/${ownKey}` : ownKey

  return {
    ...item,
    id,
    treeKey,
    parentTreeKey: parent?.treeKey,
    categoryInfoId,
    b8ShareCategoryInfoId:
      nodeType === 'category'
        ? item.b8ShareCategoryInfoId
          ? String(item.b8ShareCategoryInfoId)
          : undefined
        : parent?.b8ShareCategoryInfoId,
    serviceId: serviceId !== undefined && Number.isFinite(serviceId) ? serviceId : undefined,
    subServiceId:
      subServiceId !== undefined && Number.isFinite(subServiceId) ? subServiceId : undefined,
    nodeType,

    nameEn:
      item.nameEn ??
      item.uniqueNameEn ??
      item.serviceNameEn ??
      item.subServiceNameEn ??
      item.subSubServiceNameEn ??
      item.categoryNameEn ??
      item.name ??
      '',

    nameAr:
      item.nameAr ??
      item.uniqueNameAr ??
      item.serviceNameAr ??
      item.subServiceNameAr ??
      item.subSubServiceNameAr ??
      item.categoryNameAr ??
      '',

    durationMinutes:
      item.resolvedDuration ??
      item.durationMinutes ??
      item.visitDuration,
    duration: item.duration ?? item.visitDuration,

    price:
      item.price ??
      item.servicePrice,

    receiveB8akOrders: item.receiveB8akOrders,

    isActive: item.isActive ?? item.status,

    hasBookingPage: item.hasBookingPage,

    source,

    // B8ak children of a removed parent are removed with it; custom children stay editable.
    isRemovedFromB8ak:
      item.isRemoveFromB8ak === true || (source === 'b8ak' && Boolean(parent?.isRemovedFromB8ak)),

    showRemovedLabel:
      item.isRemoveFromB8ak === true && !parent?.isRemovedFromB8ak,

    children:
      nodeType === 'subSubService'
        ? []
        : undefined,

    canAddChild: nodeType !== 'subSubService',

    // Missing flag (older API) keeps the node expandable rather than hiding real children.
    hasChildren:
      nodeType !== 'subSubService' &&
      (item.hasService ?? item.hasSubService ?? item.hasSubSubService ?? true) !== false,
  }
}

const findNodeByTreeKey = (nodes: ApiTreeNode[], treeKey: string): ApiTreeNode | null =>
  nodes.reduce<ApiTreeNode | null>((found, node) => {
    if (found) return found
    if (node.treeKey === treeKey) return node
    return node.children?.length ? findNodeByTreeKey(node.children as ApiTreeNode[], treeKey) : null
  }, null)

// CNT-152 - an inactive node locks its own descendants' Active toggles (you
// can't activate a child while its parent is off), but never locks itself.
const isInactive = (node: ApiTreeNode): boolean =>
  node.isActive !== undefined && node.isActive !== null && !node.isActive

const getChildStatusLock = (
  parentLock: ApiTreeNode | null,
  node: ApiTreeNode
): ApiTreeNode | null => parentLock ?? (isInactive(node) ? node : null)

const getParentStatusLock = (nodes: ApiTreeNode[], node: ApiTreeNode): ApiTreeNode | null => {
  const parent = node.parentTreeKey ? findNodeByTreeKey(nodes, node.parentTreeKey) : null
  return parent ? getStatusLock(nodes, parent) : null
}

// The effective lock a NEW child under `node` would inherit - unlike
// getParentStatusLock, this also counts `node` itself being inactive.
const getStatusLock = (nodes: ApiTreeNode[], node: ApiTreeNode): ApiTreeNode | null =>
  getChildStatusLock(getParentStatusLock(nodes, node), node)

const updateNodeChildren = (
  nodes: ApiTreeNode[],
  parentTreeKey: string,
  children: ApiTreeNode[]
): ApiTreeNode[] =>
  nodes.map((node) => {
    if (node.treeKey === parentTreeKey) {
      // Keep the flag in sync after adding the first child or removing the last one.
      return {...node, children, hasChildren: children.length > 0}
    }

    if (node.children?.length) {
      return {
        ...node,
        children: updateNodeChildren(node.children as ApiTreeNode[], parentTreeKey, children),
      }
    }

    return node
  })

const updateNodeFields = (
  nodes: ApiTreeNode[],
  treeKey: string,
  patch: Partial<ApiTreeNode>
): ApiTreeNode[] =>
  nodes.map((node) => {
    if (node.treeKey === treeKey) {
      return {...node, ...patch}
    }

    if (node.children?.length) {
      return {
        ...node,
        children: updateNodeFields(node.children as ApiTreeNode[], treeKey, patch),
      }
    }

    return node
  })

const getDescendantIds = (node: ApiTreeNode): string[] =>
  (node.children as ApiTreeNode[] | undefined)?.reduce<string[]>(
    (ids, child) => [...ids, child.treeKey, ...getDescendantIds(child)],
    []
  ) ?? []

const resetNodeChildren = (nodes: ApiTreeNode[], nodeTreeKey: string): ApiTreeNode[] =>
  nodes.map((node) => {
    if (node.treeKey === nodeTreeKey) {
      return {
        ...node,
        children: undefined,
      }
    }

    if (node.children?.length) {
      return {
        ...node,
        children: resetNodeChildren(node.children as ApiTreeNode[], nodeTreeKey),
      }
    }

    return node
  })

const removeRecordKeys = <T,>(
  record: Record<string, T>,
  keysToRemove: Set<string>
): Record<string, T> =>
  Object.keys(record).reduce<Record<string, T>>((result, key) => {
    if (!keysToRemove.has(key)) {
      result[key] = record[key]
    }

    return result
  }, {})

interface ServiceTreeProps {
  reloadToken?: number
  // Reports how many categories hold at least one service (null when the request fails)
  onCategoriesLoaded?: (count: number | null) => void
}

const ServiceTree: React.FC<ServiceTreeProps> = ({reloadToken = 0, onCategoriesLoaded}) => {
  const {t, i18n} = useTranslation()
  const dispatch = useAppDispatch()
  const [filter, setFilter] = useState<CategoryFilter>('ALL')
  const [treeData, setTreeData] = useState<ApiTreeNode[]>([])
  const [expandedIds, setExpandedIds] = useState<string[]>([])
  const [loadingIds, setLoadingIds] = useState<Record<string, boolean>>({})
  const [statusPendingIds, setStatusPendingIds] = useState<Record<string, boolean>>({})
  const [editingCategory, setEditingCategory] = useState<ApiTreeNode | null>(null)
  const [editingService, setEditingService] = useState<ApiTreeNode | null>(null)
  // The node the + was pressed on; the new child sits one level below it.
  const [addingUnder, setAddingUnder] = useState<ApiTreeNode | null>(null)
  const [isLoadingCategories, setIsLoadingCategories] = useState<boolean>(false)
  // Only the unfiltered (All) load can tell whether any service exists at all.
  const [hasAnyServices, setHasAnyServices] = useState<boolean>(false)
  const lastCategoryLoadRef = useRef<string | null>(null)
  const lastFilterRef = useRef<CategoryFilter>(filter)

  const onCategoriesLoadedRef = useRef(onCategoriesLoaded)
  onCategoriesLoadedRef.current = onCategoriesLoaded

  const expandedIdsRef = useRef<string[]>(expandedIds)
  expandedIdsRef.current = expandedIds

  const reloadExpandedChildrenRef = useRef<(children: ApiTreeNode[]) => void>(() => { })

  const loadCategories = useCallback(() => {
    setIsLoadingCategories(true)

    dispatch(
      // B8ak Services sends sourceType 'B8AK', My Services sends 'EXTERNAL'; All sends none.
      serviceManagementAction.getCategoryList({
        data: filter !== 'ALL' ? { sourceType: filter } : {},
        callback: (res: any | null, err: any) => {
          setIsLoadingCategories(false)

          if (err || !res) {
            setTreeData([])
            onCategoriesLoadedRef.current?.(null)
            return
          }

          const categories = res.map((item: any, index: number) =>
            mapApiNode(item, 'category', undefined, index)
          )
          // Categories with no service at all (hasService: false) do not count as content;
          // a missing flag is treated as having services so nothing real is hidden.
          const categoriesWithServices = res.filter(
            (item: any) => item.hasService !== false
          ).length
          setTreeData(categories)
          // Only the unfiltered list says whether the company has any service; a filtered tab
          // that comes back empty must not lock the tabs and trap the user in it.
          if (filter === 'ALL') setHasAnyServices(categoriesWithServices > 0)
          reloadExpandedChildrenRef.current(categories)
          onCategoriesLoadedRef.current?.(categoriesWithServices)
        },
      })
    )
    dispatch(
          serviceManagementAction.setServiceDefault({
            callback: (
              res: any,
              err: any
            ) => {
    
              if (err) {
                setError(
                  typeof err === 'string'
                    ? err
                    : err?.message ||
                    'Failed to load catalogue.'
                )
                return
              }
            },
          })
        )
  }, [filter, dispatch])

  useEffect(() => {
    const loadKey = `${filter}|${reloadToken}`

    if (lastCategoryLoadRef.current === loadKey) {
      return
    }

    if (lastFilterRef.current !== filter) {
      lastFilterRef.current = filter
      setExpandedIds([])
      expandedIdsRef.current = []
    }

    lastCategoryLoadRef.current = loadKey
    loadCategories()
  }, [filter, reloadToken, loadCategories])

  const setNodeLoading = (nodeId: string, loading: boolean) => {
    setLoadingIds((current) => ({...current, [nodeId]: loading}))
  }

  const getSourceTypePayload = () => (filter !== 'ALL' ? {sourceType: filter} : {})

  const setNodeChildren = (treeKey: string, children: ApiTreeNode[]) =>
    setTreeData((current) =>
      updateNodeFields(updateNodeChildren(current, treeKey, children), treeKey, {
        hasChildren: children.length > 0,
      })
    )


  const loadServices = (categoryNode: ApiTreeNode) => {
    setNodeLoading(categoryNode.treeKey, true)

    dispatch(
      serviceManagementAction.getServiceByCategoryId({
        data: {
          categoryInfoId: categoryNode.categoryInfoId,
          ...getSourceTypePayload(),
        },

        callback: (res, err) => {
          setNodeLoading(categoryNode.treeKey, false)

          if (err || !res) return

          const children = res.map((item: any, index: number) =>
            mapApiNode(item, 'service', categoryNode, index)
          )

          setNodeChildren(categoryNode.treeKey, children)
          reloadExpandedChildren(children)
        },
      })
    )
  }

  const loadSubServices = (serviceNode: ApiTreeNode) => {
    if (serviceNode.serviceId === undefined || !Number.isFinite(serviceNode.serviceId)) {
      return
    }

    setNodeLoading(serviceNode.treeKey, true)

    const data: GetSubServiceRequest = {
      categoryInfoId: serviceNode.categoryInfoId,
      serviceId: serviceNode.serviceId,
      ...getSourceTypePayload(),
    }

    dispatch(
      serviceManagementAction.getSubServiceByServiceId({
        data,

        callback: (res, err) => {
          setNodeLoading(serviceNode.treeKey, false)

          if (err || !res) return

          const children = res.map((item: any, index: number) =>
            mapApiNode(item, 'subService', serviceNode, index)
          )

          setNodeChildren(serviceNode.treeKey, children)
          reloadExpandedChildren(children)
        },
      })
    )
  }

  const loadSubSubServices = (subServiceNode: ApiTreeNode) => {
    if (
      subServiceNode.subServiceId === undefined ||
      !Number.isFinite(subServiceNode.subServiceId)
    ) {
      return
    }

    setNodeLoading(subServiceNode.treeKey, true)

    const data: GetSubSubServiceRequest = {
      categoryInfoId: subServiceNode.categoryInfoId,
      subServiceId: subServiceNode.subServiceId,

      ...getSourceTypePayload(),
    }

    dispatch(
      serviceManagementAction.getSubSubServiceBySubServiceId({
        data,

        callback: (res, err) => {
          setNodeLoading(subServiceNode.treeKey, false)

          if (err || !res) return

          const children = res.map((item: any, index: number) =>
            mapApiNode(item, 'subSubService', subServiceNode, index)
          )

          setNodeChildren(subServiceNode.treeKey, children)
        },
      })
    )
  }

  const reloadExpandedChildren = (children: ApiTreeNode[]) => {
    children.forEach((child) => {
      if (!expandedIdsRef.current.includes(child.treeKey)) return

      switch (child.nodeType) {
        case 'category':
          loadServices(child)
          break

        case 'service':
          loadSubServices(child)
          break

        case 'subService':
          loadSubSubServices(child)
          break

        default:
          break
      }
    })
  }

  reloadExpandedChildrenRef.current = reloadExpandedChildren

  // Refetch a node's children, opening it first when the new child would be hidden.
  const reloadChildren = (parent: ApiTreeNode) => {
    if (!expandedIds.includes(parent.treeKey)) {
      setExpandedIds((current) => current.concat(parent.treeKey))
    }

    switch (parent.nodeType) {
      case 'category':
        loadServices(parent)
        break

      case 'service':
        loadSubServices(parent)
        break

      case 'subService':
        loadSubSubServices(parent)
        break

      default:
        break
    }
  }

  // A renamed node is rendered from its parent's child list, so that is what gets refetched.
  const reloadNode = (node: ApiTreeNode) => {
    const parent = node.parentTreeKey ? findNodeByTreeKey(treeData, node.parentTreeKey) : null

    if (parent) reloadChildren(parent)
  }

  const toggleExpanded = (node: ApiTreeNode) => {
    const isExpanded = expandedIds.includes(node.treeKey)

    if (isExpanded) {
      const idsToReset = new Set([node.treeKey, ...getDescendantIds(node)])

      // Close this node and every expanded descendant.
      setExpandedIds((current) => current.filter((id) => !idsToReset.has(id)))

      // Remove the fetched branch. Reopening the node will call its API again.
      setTreeData((current) => resetNodeChildren(current, node.treeKey))

      // Clear temporary state belonging to the removed descendants.
      setLoadingIds((current) => removeRecordKeys(current, idsToReset))

      return
    }

    // The previous collapse reset children to undefined, so each reopen
    // starts with a fresh API request and fresh child data.
    setExpandedIds((current) => current.concat(node.treeKey))

    switch (node.nodeType) {
      case 'category':
        loadServices(node)
        break

      case 'service':
        loadSubServices(node)
        break

      case 'subService':
        loadSubSubServices(node)
        break

      default:
        break
    }
  }

  const isArabic = i18n.language?.toLowerCase().startsWith('ar')

  const getStatusLockMessage = (lock: ApiTreeNode | null): string | undefined =>
    lock
      ? t('SERVICE_MANAGEMENT_PAGE.SERVICE_TREE.ACTIVE_LOCKED_BY_PARENT', {
          name: isArabic ? lock.nameAr || lock.nameEn : lock.nameEn,
        })
      : undefined

  const renderRows = (
    nodes: ApiTreeNode[],
    depth: number,
    parentLock: ApiTreeNode | null = null
  ): React.ReactNode[] =>
    nodes.reduce<React.ReactNode[]>((rows, node) => {
      const isExpanded = expandedIds.includes(node.treeKey)
      const canExpand =
        node.nodeType !== 'subSubService' &&
        node.hasChildren !== false
      const isLoading = Boolean(loadingIds[node.treeKey])
      const isCategory = node.nodeType === 'category'
      const receiveOn = Boolean(node.receiveB8akOrders)
      const activeOn = Boolean(node.isActive)
      const isStatusPending = Boolean(statusPendingIds[node.treeKey])

      const setStatusPending = (treeKey: string, pending: boolean) =>
        setStatusPendingIds((current) => ({...current, [treeKey]: pending}))

      const updateCategoryStatus = (node: ApiTreeNode, status: boolean) => {
        setStatusPending(node.treeKey, true)

        dispatch(
          serviceManagementAction.updateCentroCategoryStatus({
            data: {
              id: node.id,
              status,
            },

            callback: (res, err) => {
              setStatusPending(node.treeKey, false)

              if (err || res === null) return

              setTreeData((current) => updateNodeFields(current, node.treeKey, {isActive: status}))
            },
          })
        )
      }

      const updateServiceStatus = (node: ApiTreeNode, status: boolean) => {
        setStatusPending(node.treeKey, true)

        dispatch(
          serviceManagementAction.updateCentroCategoryServiceStatus({
            data: {
              id: node.id,
              status,
              serviceLevel: getServiceLevel(node),
            },

            callback: (res, err) => {
              setStatusPending(node.treeKey, false)

              if (err || res === null) return

              setTreeData((current) => updateNodeFields(current, node.treeKey, {isActive: status}))
            },
          })
        )
      }
      const hasVisibleChildren = isExpanded && Boolean(node.children?.length)

      rows.push(
        <div
          className={`service-tree__row service-tree__row--depth-${depth}${hasVisibleChildren ? ' service-tree__row--no-border' : ''
            }${node.isRemovedFromB8ak ? ' service-tree__row--removed' : ''}`}
          key={node.treeKey}
        >
          <div
            className='service-tree__cell service-tree__cell--service'
            style={{paddingInlineStart: `${13 + depth * 26}px`}}
          >
            {canExpand ? (
              <button
                type='button'
                className={`service-tree__chevron${isExpanded ? ' service-tree__chevron--expanded' : ' service-tree__chevron--collapsed'
                  }${isLoading ? ' service-tree__chevron--loading' : ''}`}
                aria-expanded={isExpanded}
                aria-label={t(
                  isExpanded
                    ? 'SERVICE_MANAGEMENT_PAGE.SERVICE_TREE.COLLAPSE'
                    : 'SERVICE_MANAGEMENT_PAGE.SERVICE_TREE.EXPAND'
                )}
                disabled={isLoading}
                onClick={() => toggleExpanded(node)}
              >
                {/* One icon that stays mounted: react-inlinesvg blanks the icon while it reloads
                    a changed src, so the open state rotates it and loading only dims it. */}
                <KTSVG defaultColor={true} path={ICONS.chevronRight} />
              </button>
            ) : (
              <span className='service-tree__chevron-spacer' />
            )}

            <span
              className={`service-tree__name${isCategory ? ' service-tree__name--category' : ''}`}
              title={isArabic ? node.nameAr || node.nameEn : node.nameEn}
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {isArabic ? node.nameAr || node.nameEn : node.nameEn}
            </span>
            {/* Every service level shows its source; Categories have none */}
            {depth >= 1 && (<span className={`service-tree__badge service-tree__badge--${node.source}`}>
              {t(
                node.source === 'b8ak'
                  ? 'SERVICE_MANAGEMENT_PAGE.SERVICE_TREE.BADGE_B8AK'
                  : 'SERVICE_MANAGEMENT_PAGE.SERVICE_TREE.BADGE_CUSTOM'
              )}
            </span>
            )}

            {node.showRemovedLabel && (
              <span className='service-tree__removed-label'>
                {t(
                  isCategory
                    ? 'SERVICE_MANAGEMENT_PAGE.SERVICE_TREE.NO_LONGER_IN_B8AK_CATEGORY'
                    : 'SERVICE_MANAGEMENT_PAGE.SERVICE_TREE.NO_LONGER_IN_B8AK'
                )}
              </span>
            )}
          </div>

          <div className='service-tree__cell service-tree__cell--duration'>
            {(node.duration ?? node.durationMinutes) != null &&
              t('SERVICE_MANAGEMENT_PAGE.SERVICE_TREE.DURATION', {
                minutes: node.duration ?? node.durationMinutes,
              })}
          </div>

          <div className='service-tree__cell service-tree__cell--price'>
            {node.price != null &&
              t('SERVICE_MANAGEMENT_PAGE.SERVICE_TREE.PRICE', {price: node.price})}
          </div>

          <div className='service-tree__cell service-tree__cell--center'>
            {/* Receive B8ak orders exists only on B8ak services, at every level. */}
            {node.source === 'b8ak' &&
              node.nodeType !== 'category' &&
              node.receiveB8akOrders !== undefined && (
              <Toggle
                checked={receiveOn}
                // A service no longer in the B8ak catalogue keeps its current value, at every level.
                readOnly={node.isRemovedFromB8ak}
                label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_TREE.TH_RECEIVE_B8AK_ORDERS')}
                onChange={() =>
                  setTreeData((current) =>
                    updateNodeFields(current, node.treeKey, {
                      receiveB8akOrders: !receiveOn,
                    })
                  )
                }
              />
            )}
          </div>

          <div className='service-tree__cell service-tree__cell--center'>
            {node.isActive !== undefined && (
              <Toggle
                checked={activeOn}
                disabled={isStatusPending}
                readOnly={node.isRemovedFromB8ak}
                locked={parentLock !== null}
                label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_TREE.TH_ACTIVE')}
                onChange={() => {
                  if (parentLock) {
                    toast.warning(getStatusLockMessage(parentLock), {
                      ...ToastDefaultConfig(),
                      toastId: parentLock.treeKey,
                    })
                    return
                  }

                  const nextStatus = !activeOn

                  if (node.nodeType === 'category') {
                    updateCategoryStatus(node, nextStatus)
                  } else {
                    updateServiceStatus(node, nextStatus)
                  }
                }}
              />
            )}
          </div>

          <div className='service-tree__cell service-tree__cell--center'>
            {node.hasBookingPage && (
              <span
                className='service-tree__lock'
                title={t('SERVICE_MANAGEMENT_PAGE.SERVICE_TREE.BOOKING_PAGE_LOCKED')}
              >
                <KTSVG defaultColor={true} path={ICONS.lock} />
              </span>
            )}
          </div>

          <div className='service-tree__cell service-tree__cell--actions'>
            <button
              type='button'
              className='service-tree__icon-button'
              aria-label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_TREE.EDIT')}
              disabled={isLoading}
              onClick={() => (isCategory ? setEditingCategory(node) : setEditingService(node))}
            >
              <KTSVG defaultColor={true} path={ICONS.pencil} />
            </button>
            {node.canAddChild && (
              <button
                type='button'
                className='service-tree__icon-button'
                aria-label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_TREE.ADD')}
                title={
                  canAddChild(node)
                    ? t('SERVICE_MANAGEMENT_PAGE.SERVICE_TREE.ADD')
                    : t('SERVICE_MANAGEMENT_PAGE.SERVICE_TREE.ADD_PARENT_NOT_READY')
                }
                disabled={!canAddChild(node) || isLoading}
                onClick={() => setAddingUnder(node)}
              >
                <KTSVG defaultColor={true} path={ICONS.plus} />
              </button>
            )}
          </div>
        </div>
      )

      if (isExpanded && node.children?.length) {
        rows.push(
          ...renderRows(
            node.children as ApiTreeNode[],
            depth + 1,
            getChildStatusLock(parentLock, node)
          )
        )
      } else if (canExpand && isExpanded && !isLoading && node.children?.length === 0) {
        // EC-008 - the filter hides every child of this parent.
        rows.push(
          <div
            key={`${node.treeKey}__empty`}
            className='service-tree__row service-tree__row--empty-children'
          >
            <div
              className='service-tree__cell service-tree__cell--service'
              style={{paddingInlineStart: `${13 + (depth + 1) * 26}px`}}
            >
              {t('SERVICE_MANAGEMENT_PAGE.SERVICE_TREE.EMPTY_FILTER')}
            </div>
          </div>
        )
      }

      return rows
    }, [])

  return (
    <div className='service-tree'>
      <ServiceSourceFilters
        value={filter}
        sourceFiltersDisabled={!hasAnyServices}
        onChange={setFilter}
      />

      <div
        className='service-tree__table-scroll'
        style={{
          width: '100%',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <div className='service-tree__card' style={{minWidth: '980px'}}>
          <div className='service-tree__row service-tree__row--header'>
            {COLUMN_KEYS.map((key, index) => (
              <div
                key={key}
                className={`service-tree__cell service-tree__cell--th service-tree__cell--th-${index}`}
                style={{whiteSpace: 'nowrap'}}
              >
                {t(key)}
              </div>
            ))}
          </div>

          {isLoadingCategories ? (
            <div className='service-tree__empty'>
              <span className='spinner-border spinner-border-sm' />
            </div>
          ) : treeData.length > 0 ? (
            renderRows(treeData, 0)
          ) : (
            <div className='service-tree__empty'>
              {t('SERVICE_MANAGEMENT_PAGE.SERVICE_TREE.EMPTY_FILTER')}
            </div>
          )}
        </div>
      </div>

      {addingUnder && getChildLevel(addingUnder) === 1 && (
        <AddMajorServiceModal
          isShow
          categoryInfoId={addingUnder.categoryInfoId}
          b8ShareCategoryInfoId={addingUnder.b8ShareCategoryInfoId}
          statusLockMessage={getStatusLockMessage(getStatusLock(treeData, addingUnder))}
          onHide={() => setAddingUnder(null)}
          onSaved={(savedCategoryId) =>
            reloadChildren(
              treeData.find((node) => node.b8ShareCategoryInfoId === savedCategoryId) ?? addingUnder
            )
          }
        />
      )}
      {addingUnder && getChildLevel(addingUnder) === 2 && (
        <AddSubServiceModal
          isShow
          serviceLevel={2}
          categoryInfoId={addingUnder.categoryInfoId}
          parentServiceId={addingUnder.serviceId}
          statusLockMessage={getStatusLockMessage(getStatusLock(treeData, addingUnder))}
          onHide={() => setAddingUnder(null)}
          onSaved={() => reloadChildren(addingUnder)}
        />
      )}
      {addingUnder && getChildLevel(addingUnder) === 3 && (
        <AddSubSubServiceModal
          isShow
          categoryInfoId={addingUnder.categoryInfoId}
          parentServiceId={addingUnder.serviceId}
          parentSubServiceId={addingUnder.subServiceId}
          statusLockMessage={getStatusLockMessage(getStatusLock(treeData, addingUnder))}
          onHide={() => setAddingUnder(null)}
          onSaved={() => reloadChildren(addingUnder)}
        />
      )}
      <ServiceCategoryModal
        isShow={editingCategory !== null}
        mode='edit'
        categoryId={editingCategory?.categoryInfoId ?? editingCategory?.id}
        initialValues={
          editingCategory
            ? {
                nameEn: editingCategory.nameEn,
                isActive: editingCategory.isActive !== false,
              }
            : undefined
        }
        onHide={() => setEditingCategory(null)}
        onSaved={loadCategories}
      />
      {editingService && getServiceLevel(editingService) === 1 && (
        <AddMajorServiceModal
          isShow
          mode='edit'
          categoryInfoId={editingService.categoryInfoId}
          b8ShareCategoryInfoId={editingService.b8ShareCategoryInfoId}
          nodeId={editingService.id}
          serviceId={getServiceNumericId(editingService) ?? undefined}
          isCustom={editingService.source === 'custom'}
          isRemovedFromB8ak={editingService.isRemovedFromB8ak}
          initialNameEn={editingService.nameEn}
          initialNameAr={editingService.nameAr}
          initialStatus={editingService.isActive !== false}
          initialIcon={editingService.icon}
          initialWorkersNeeded={editingService.workersNeeded ?? editingService.resolvedWorkersNeeded}
          initialDuration={editingService.duration}
          initialPrice={editingService.price}
          statusLockMessage={getStatusLockMessage(getParentStatusLock(treeData, editingService))}
          onHide={() => setEditingService(null)}
          onSaved={(movedToCategoryId) => {
            reloadNode(editingService)
            // A Major moved to another Category also shows up under its new parent.
            const target = movedToCategoryId
              ? treeData.find(
                (node) =>
                  node.nodeType === 'category' && node.b8ShareCategoryInfoId === movedToCategoryId
              )
              : undefined
            if (target && target.treeKey !== editingService.parentTreeKey) reloadChildren(target)
          }}
        />
      )}
      {editingService && getServiceLevel(editingService) === 2 && (
        <AddSubServiceModal
          isShow
          mode='edit'
          serviceLevel={2}
          categoryInfoId={editingService.categoryInfoId}
          nodeId={editingService.id}
          serviceId={getServiceNumericId(editingService) ?? undefined}
          parentServiceId={editingService.serviceId}
          isCustom={editingService.source === 'custom'}
          isRemovedFromB8ak={editingService.isRemovedFromB8ak}
          catalogueDuration={editingService.duration ?? editingService.durationMinutes}
          catalogueWorkersNeeded={
            editingService.workersNeeded ?? editingService.resolvedWorkersNeeded
          }
          initialNameEn={editingService.nameEn}
          initialNameAr={editingService.nameAr}
          initialStatus={editingService.isActive !== false}
          statusLockMessage={getStatusLockMessage(getParentStatusLock(treeData, editingService))}
          onHide={() => setEditingService(null)}
          onSaved={() => reloadNode(editingService)}
        />
      )}
      {editingService && getServiceLevel(editingService) === 3 && (
        <AddSubSubServiceModal
          isShow
          mode='edit'
          categoryInfoId={editingService.categoryInfoId}
          nodeId={editingService.id}
          serviceId={getServiceNumericId(editingService) ?? undefined}
          parentServiceId={editingService.serviceId}
          parentSubServiceId={editingService.subServiceId}
          isCustom={editingService.source === 'custom'}
          isRemovedFromB8ak={editingService.isRemovedFromB8ak}
          catalogueDuration={editingService.duration ?? editingService.durationMinutes}
          catalogueWorkersNeeded={
            editingService.workersNeeded ?? editingService.resolvedWorkersNeeded
          }
          initialNameEn={editingService.nameEn}
          initialNameAr={editingService.nameAr}
          initialStatus={editingService.isActive !== false}
          statusLockMessage={getStatusLockMessage(getParentStatusLock(treeData, editingService))}
          onHide={() => setEditingService(null)}
          onSaved={() => reloadNode(editingService)}
        />
      )}
    </div>
  )
}

export default ServiceTree
